// Audio utilities: mic capture (24kHz PCM16) + streaming PCM playback

export const TARGET_RATE = 24000;

export function getWsBase() {
  // Same-origin by default: works on preview, deployment AND custom domains
  // (ingress routes /api/* to the backend on the same host, e.g. translate.dijitalroket.com)
  const base = window.location.origin || process.env.REACT_APP_BACKEND_URL;
  return base.replace(/^http/, "ws");
}

export function floatTo16(f32) {
  const i16 = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    i16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return i16;
}

export function int16ToB64(i16) {
  const bytes = new Uint8Array(i16.buffer, i16.byteOffset, i16.byteLength);
  let bin = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CH));
  }
  return btoa(bin);
}

export function b64ToF32(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const i16 = new Int16Array(bytes.buffer);
  const f32 = new Float32Array(i16.length);
  for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
  return f32;
}

export function downsample(f32, fromRate, toRate = TARGET_RATE) {
  if (fromRate === toRate) return f32;
  const ratio = fromRate / toRate;
  const outLen = Math.floor(f32.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const idx = i * ratio;
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, f32.length - 1);
    out[i] = f32[i0] + (f32[i1] - f32[i0]) * (idx - i0);
  }
  return out;
}

const WORKLET_CODE = `
class PCMCapture extends AudioWorkletProcessor {
  constructor() { super(); this._buf = []; this._len = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) {
      this._buf.push(new Float32Array(ch));
      this._len += ch.length;
      if (this._len >= 2048) {
        const merged = new Float32Array(this._len);
        let o = 0;
        for (const b of this._buf) { merged.set(b, o); o += b.length; }
        this.port.postMessage(merged, [merged.buffer]);
        this._buf = []; this._len = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-capture', PCMCapture);
`;

// Start microphone capture. onChunk(base64Pcm16_24k, rmsLevel 0..1)
export async function startMicCapture(onChunk) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  await ctx.resume();
  const src = ctx.createMediaStreamSource(stream);

  const handleFrame = (f32) => {
    let sum = 0;
    for (let i = 0; i < f32.length; i++) sum += f32[i] * f32[i];
    const rms = Math.sqrt(sum / f32.length);
    const ds = downsample(f32, ctx.sampleRate);
    onChunk(int16ToB64(floatTo16(ds)), Math.min(1, rms * 4));
  };

  let node = null;
  let fallback = null;
  if (ctx.audioWorklet) {
    const url = URL.createObjectURL(new Blob([WORKLET_CODE], { type: "application/javascript" }));
    await ctx.audioWorklet.addModule(url);
    node = new window.AudioWorkletNode(ctx, "pcm-capture");
    node.port.onmessage = (e) => handleFrame(e.data);
    src.connect(node);
    const mute = ctx.createGain();
    mute.gain.value = 0;
    node.connect(mute);
    mute.connect(ctx.destination);
  } else {
    // ScriptProcessor fallback (older Safari)
    fallback = ctx.createScriptProcessor(2048, 1, 1);
    fallback.onaudioprocess = (e) => handleFrame(new Float32Array(e.inputBuffer.getChannelData(0)));
    src.connect(fallback);
    const mute = ctx.createGain();
    mute.gain.value = 0;
    fallback.connect(mute);
    mute.connect(ctx.destination);
  }

  return {
    stop() {
      try { if (node) { node.port.onmessage = null; node.disconnect(); } } catch (e) {}
      try { if (fallback) { fallback.onaudioprocess = null; fallback.disconnect(); } } catch (e) {}
      try { src.disconnect(); } catch (e) {}
      stream.getTracks().forEach((t) => t.stop());
      try { ctx.close(); } catch (e) {}
    },
  };
}

// Streaming PCM16 24kHz player with sequential scheduling (low latency)
export function createPcmPlayer() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  const gain = ctx.createGain();
  gain.gain.value = 1;
  gain.connect(ctx.destination);
  let nextTime = 0;
  let active = true;

  return {
    ctx,
    async resume() {
      await ctx.resume();
    },
    setMuted(muted) {
      // smooth ramp avoids clicks
      const t = ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setTargetAtTime(muted ? 0 : 1, t, 0.02);
    },
    push(b64) {
      if (!active) return;
      const f32 = b64ToF32(b64);
      if (!f32.length) return;
      const buf = ctx.createBuffer(1, f32.length, TARGET_RATE);
      buf.copyToChannel(f32, 0);
      const srcNode = ctx.createBufferSource();
      srcNode.buffer = buf;
      srcNode.connect(gain);
      const now = ctx.currentTime;
      if (nextTime < now + 0.08) nextTime = now + 0.08; // tiny jitter buffer
      srcNode.start(nextTime);
      nextTime += buf.duration;
    },
    reset() {
      nextTime = 0;
    },
    close() {
      active = false;
      try { ctx.close(); } catch (e) {}
    },
  };
}
